import React, { Suspense } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { BackofficeShell } from './components';
import ChatymeLoading from './components/common/ChatymeLoading';
import { AuthProvider, useAuthContext } from './contexts/AuthContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { UserHelper } from './helpers/UserHelper';
import './styles/app.css';
import { UserRole } from './types/user';
import { ChunkLoadErrorBoundary, lazyWithChunkReload } from './utils/chunkLoadRecovery';

// Use React.lazy for code splitting; reload once when cached chunks are stale after deploy.
const LoginScreen = lazyWithChunkReload(() => import('./screens/LoginScreen'));
const RegisterScreen = lazyWithChunkReload(() => import('./screens/RegisterScreen'));
const ClientDashboardScreen = lazyWithChunkReload(() => import('./screens/client/ClientDashboardScreen'));
const BackofficeDashboardScreen = lazyWithChunkReload(() => import('./screens/backoffice/BackofficeDashboardScreen'));
const BackofficeUsersScreen = lazyWithChunkReload(() => import('./screens/backoffice/BackofficeUsersScreen'));
const BackofficeMessagesScreen = lazyWithChunkReload(() => import('./screens/backoffice/BackofficeMessagesScreen'));
const BackofficeAiHubScreen = lazyWithChunkReload(() => import('./screens/backoffice/BackofficeAiHubScreen'));
const BackofficeBusinessHoursScreen = lazyWithChunkReload(() => import('./screens/backoffice/BackofficeBusinessHoursScreen'));
const BackofficeReportsScreen = lazyWithChunkReload(() => import('./screens/backoffice/BackofficeReportsScreen'));
const BackofficeSchedulerScreen = lazyWithChunkReload(() => import('./screens/backoffice/BackofficeSchedulerScreen'));
const BackofficeQueueDashboardScreen = lazyWithChunkReload(() => import('./screens/backoffice/BackofficeQueueDashboardScreen'));
const AgentChatBox = lazyWithChunkReload(() => import('./screens/chat/AgentChatBox'));
const ClientChatBox = lazyWithChunkReload(() => import('./screens/chat/ClientChatBox'));
const ErrorScreen = lazyWithChunkReload(() => import('./screens/ErrorScreen'));
const UnauthorizedScreen = lazyWithChunkReload(() => import('./screens/UnauthorizedScreen'));

const protectedRoutes = [
  {
    path: '/client',
    component: ClientDashboardScreen,
    roles: [UserRole.Client]
  },
  {
    path: '/agent-chat',
    component: AgentChatBox,
    roles: [UserRole.Agent]
  },
  {
    path: '/backoffice',
    component: BackofficeDashboardScreen,
    roles: [UserRole.Agent, UserRole.Panel, UserRole.Admin]
  },
  {
    path: '/backoffice-users',
    component: BackofficeUsersScreen,
    roles: [UserRole.Admin]
  },
  {
    path: '/backoffice-messages',
    component: BackofficeMessagesScreen,
    roles: [UserRole.Panel, UserRole.Admin]
  },
  {
    path: '/backoffice-ai-hub',
    component: BackofficeAiHubScreen,
    roles: [UserRole.Admin]
  },
  {
    path: '/backoffice-business-hours',
    component: BackofficeBusinessHoursScreen,
    roles: [UserRole.Admin]
  },
  {
    path: '/backoffice-reports',
    component: BackofficeReportsScreen,
    roles: [UserRole.Panel, UserRole.Admin]
  },
  {
    path: '/backoffice-scheduler',
    component: BackofficeSchedulerScreen,
    roles: [UserRole.Panel, UserRole.Admin]
  },
  {
    path: '/backoffice-queue-monitor',
    component: BackofficeQueueDashboardScreen,
    roles: [UserRole.Admin]
  },
]


// Helper function to check if JWT token is expired
const isTokenExpired = (token: string | null): boolean => {
  if (!token) {
    return true;
  }

  try {
    // JWT tokens have 3 parts separated by dots: header.payload.signature
    const parts = token.split('.');
    if (parts.length !== 3) {
      return true;
    }

    // Decode the payload (second part)
    const payload = JSON.parse(atob(parts[1]));

    // Check if token has expiration claim
    if (!payload.exp) {
      return false; // If no exp claim, assume token is valid
    }

    // exp is in seconds, Date.now() is in milliseconds
    const expirationTime = payload.exp * 1000;
    const currentTime = Date.now();

    return currentTime >= expirationTime;
  } catch (error) {
    console.error('Error checking token expiration:', error);
    return true; // If we can't decode, assume expired for safety
  }
};

// Separate component for routes to use the auth context
const AppRoutes: React.FC = () => {
  const { isAuthenticated, userRole, isLoading, token } = useAuthContext();
  const location = useLocation();
  // Detect if this route is rendered inside an iframe (embedded)
  const isEmbed = typeof window !== 'undefined' && window.self !== window.top;

  // Determine the appropriate route based on auth state
  const getRouteElement = (path: string) => {
    // If still loading, show nothing
    if (isLoading) {
      return null;
    }

    if (isEmbed) {
      if (path === '/agent-chat') {
        return <AgentChatBox />;
      }
      if (path === '/client-chat') {
        return <ClientChatBox />;
      }
      if (path === '/error') {
        return <ErrorScreen />;
      }
      if (path === '/unauthorized') {
        return <UnauthorizedScreen />;
      }
      return null;
    }

    // Check if token is expired / missing
    if (isTokenExpired(token)) {
      // Clear stored token and auth data
      try {
        localStorage.removeItem('auth');
      } catch (error) {
        console.warn('Error clearing auth from localStorage:', error);
      }
      if (path === '/login') {
        return <LoginScreen />;
      }
      if (path === '/error') {
        return <ErrorScreen />;
      }
      if (path === '/unauthorized') {
        return <UnauthorizedScreen />;
      }
      return <Navigate to="/login" state={{ from: location }} replace />;
    }

    // Public routes
    if (path === '/login') {
      if (isAuthenticated) {
        const redirectPath = UserHelper.getHomePath(userRole);
        // Avoid same-path replace loops (e.g. system role home is "/login")
        if (redirectPath !== '/login') {
          return <Navigate to={redirectPath} replace />;
        }
        return <UnauthorizedScreen />;
      }
      return <LoginScreen />;
    }

    if (path === '/error') {
      return <ErrorScreen />;
    }

    if (path === '/unauthorized') {
      return <UnauthorizedScreen />;
    }


    if (!isAuthenticated) {
      return <Navigate to="/login" state={{ from: location }} replace />;
    }

    // ---------------- Protected routes ----------------

    if (path === '/') {
      const redirectPath = UserHelper.getHomePath(userRole);
      if (redirectPath === '/') {
        return <UnauthorizedScreen />;
      }
      return <Navigate to={redirectPath} replace />;
    }

    const protectedRoute = protectedRoutes.find(route => route.path === path);
    if (protectedRoute) {
      if (!userRole || !protectedRoute.roles.includes(userRole)) {
        return <Navigate to="/unauthorized" state={{ from: location }} replace />;
      }
      return <protectedRoute.component />;
    }

    // Fallback
    return <Navigate to="/login" replace />;
  };

  // If still loading, show nothing
  if (isLoading) {
    return null;
  }

  const backofficeShellElement = (() => {
    if (isTokenExpired(token)) {
      return <Navigate to="/login" state={{ from: location }} replace />;
    }
    if (!isAuthenticated) {
      return <Navigate to="/login" state={{ from: location }} replace />;
    }
    const accessibleMenus = UserHelper.getAccessibleBackofficeMenus(userRole);
    if (accessibleMenus.length === 0) {
      return <Navigate to="/unauthorized" state={{ from: location }} replace />;
    }
    return <BackofficeShell />;
  })();

  return (
    <ChunkLoadErrorBoundary>
      <Suspense fallback={<ChatymeLoading />}>
        <Routes>
        <Route path="/login" element={getRouteElement('/login')} />
        <Route path="/client" element={getRouteElement('/client')} />
        <Route element={backofficeShellElement}>
          <Route path="/backoffice" element={getRouteElement('/backoffice')} />
          <Route path="/backoffice-users" element={getRouteElement('/backoffice-users')} />
          <Route path="/backoffice-messages" element={getRouteElement('/backoffice-messages')} />
          <Route path="/backoffice-ai-hub" element={getRouteElement('/backoffice-ai-hub')} />
          <Route
            path="/backoffice-business-hours"
            element={getRouteElement('/backoffice-business-hours')}
          />
          <Route path="/backoffice-reports" element={getRouteElement('/backoffice-reports')} />
          <Route path="/backoffice-scheduler" element={getRouteElement('/backoffice-scheduler')} />
          <Route
            path="/backoffice-queue-monitor"
            element={getRouteElement('/backoffice-queue-monitor')}
          />
        </Route>
        <Route path="/backoffice-config" element={<Navigate to="/backoffice-business-hours" replace />} />
        <Route path="/agent-chat" element={getRouteElement('/agent-chat')} />
        <Route path="/client-chat" element={getRouteElement('/client-chat')} />
        <Route path="/error" element={getRouteElement('/error')} />
        <Route path="/unauthorized" element={getRouteElement('/unauthorized')} />
        <Route path="/" element={getRouteElement('/')} />
        <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </ChunkLoadErrorBoundary>
  );
};

// Main App component that provides the auth context
const App: React.FC = () => {
  return (
    <AuthProvider>
      <NotificationProvider>
        <AppRoutes />
      </NotificationProvider>
    </AuthProvider>
  );
};

export default App; 